import React from 'react';
import {
    useGetAllVendorSettingsQuery,
    useGetApiKeysQuery,
    useGetCatalogsQuery,
    useGetCompaniesQuery,
    useGetContactsQuery,
    useGetInstalledExtensionsQuery,
    useGetSubOrganizationsQuery,
    useGetTaxCodesQuery,
} from '../store/Api';
import { useGetWebhooksQuery } from '../store/endpoints/WebhooksEndpoints';
import { useGetStorefrontsQuery } from '../store/endpoints/StorefrontSettingsEndpoints';
import { InstanceTypeKey } from './registry';

/**
 * The lists behind "which one of these are you working on?".
 *
 * A generic `useList` map would break the rules of hooks - only one type's query may run, but hooks
 * cannot be called conditionally. Each type is therefore its own tiny component that calls exactly
 * one query unconditionally and hands the result to a render prop.
 */

export interface IInstanceOption {
    id: string;
    label: string;
    /** Shown under the label - a sub URL, an extension type, whatever disambiguates. */
    secondary?: string;
}

export interface IInstanceListState {
    options: IInstanceOption[];
    isLoading: boolean;
    isError: boolean;
}

export interface IInstanceListProps {
    /** Narrows the list, e.g. to installed extensions of one extension type. */
    filter?: string;
    children: (state: IInstanceListState) => React.ReactElement | null;
}

const byLabel = (a: IInstanceOption, b: IInstanceOption) => a.label.localeCompare(b.label);

const StorefrontInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetStorefrontsQuery();
    return children({
        options: (query.data?.items ?? [])
            .map((s) => ({ id: s.id ?? '', label: s.name || s.subUrl || (s.id ?? ''), secondary: s.subUrl }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const InstalledExtensionInstances: React.FC<IInstanceListProps> = ({ filter, children }) => {
    const query = useGetInstalledExtensionsQuery();
    return children({
        options: (query.data ?? [])
            // `source` is the catalog extension this was installed from, which is what a field id
            // names; `extensionType` is the broad category (import, realtime, ...).
            //
            // A leading "!" excludes instead. Some fields exist on only one of the two install
            // forms — Nickname is on the single-extension form and not on the folio form — so
            // offering every installed extension would let you pick one the field cannot apply to
            // and land on an honest but useless "couldn't find it on this page".
            .filter((e) => {
                if (!filter) return true;
                if (filter.startsWith('!')) {
                    const excluded = filter.slice(1);
                    return e.source !== excluded && e.extensionType !== excluded;
                }
                return e.source === filter || e.extensionType === filter;
            })
            .map((e) => ({ id: e.id, label: e.nickname || e.name, secondary: e.extensionType }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const CompanyInstances: React.FC<IInstanceListProps> = ({ children }) => {
    /*
     * The search body is required — `body: arg.query` sends nothing at all when the arg is omitted,
     * and the endpoint rejects that, which surfaced as "Could not load companies" in the picker.
     *
     * Note getCompanies keys its cache on the endpoint name alone, so this shares one cache entry
     * with the company grid. That is safe only because the picker renders on the /go resolver route,
     * where no grid is mounted; don't reuse this list on a screen that shows companies.
     */
    const query = useGetCompaniesQuery({ query: { query: '', conditions: { page: 1, pageSize: 200 } } });
    return children({
        options: (query.data?.items ?? [])
            .map((c) => ({ id: c.id ?? '', label: c.companyName || (c.id ?? ''), secondary: c.companyIdentifier }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const ContactInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetContactsQuery({ query: { query: '', conditions: { page: 1, pageSize: 200 } } });
    return children({
        // Email is the label because it's what a support conversation actually names — plenty of
        // contacts share a first/last name, and some have neither filled in.
        options: (query.data?.items ?? [])
            .map((c) => ({
                id: c.id ?? '',
                label: c.emailAddress || [c.firstName, c.lastName].filter(Boolean).join(' ') || (c.id ?? ''),
                secondary: [c.firstName, c.lastName].filter(Boolean).join(' ') || undefined,
            }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const CatalogInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetCatalogsQuery();
    return children({
        // Catalogs are keyed by name, which is also what the rest of the app passes around.
        options: (query.data ?? []).map((c) => ({ id: c.name, label: c.description || c.name })).sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const SubOrganizationInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetSubOrganizationsQuery();
    return children({
        options: (query.data ?? []).map((s) => ({ id: s.id, label: s.name || s.id })).filter((o) => o.id).sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const VendorInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetAllVendorSettingsQuery();
    return children({
        options: (query.data?.items ?? [])
            .map((v) => ({ id: v.sourceVendor, label: v.vendorDisplayName || v.sourceVendor }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const WebhookInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetWebhooksQuery({ pageSize: 200 });
    return children({
        options: (query.data?.items ?? [])
            .map((w) => ({ id: w.id ?? '', label: w.name || (w.id ?? ''), secondary: w.type }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const TaxCodeInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetTaxCodesQuery();
    return children({
        options: (query.data ?? [])
            .map((t) => ({ id: t.id ?? t.name, label: t.description || t.name }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

const ApiKeyInstances: React.FC<IInstanceListProps> = ({ children }) => {
    const query = useGetApiKeysQuery();
    return children({
        options: (query.data ?? [])
            .map((k) => ({ id: k.id, label: k.name || k.id, secondary: k.keyType }))
            .filter((o) => o.id)
            .sort(byLabel),
        isLoading: query.isLoading,
        isError: query.isError,
    });
};

export interface IInstanceTypeDefinition {
    key: InstanceTypeKey;
    label: string;
    pluralLabel: string;
    List: React.FC<IInstanceListProps>;
}

export const INSTANCE_TYPES: Record<InstanceTypeKey, IInstanceTypeDefinition> = {
    storefront: { key: 'storefront', label: 'storefront', pluralLabel: 'Storefronts', List: StorefrontInstances },
    installedExtension: {
        key: 'installedExtension',
        label: 'extension',
        pluralLabel: 'Installed Extensions',
        List: InstalledExtensionInstances,
    },
    company: { key: 'company', label: 'company', pluralLabel: 'Companies', List: CompanyInstances },
    contact: { key: 'contact', label: 'contact', pluralLabel: 'Contacts', List: ContactInstances },
    catalog: { key: 'catalog', label: 'catalog', pluralLabel: 'Catalogs', List: CatalogInstances },
    subOrganization: {
        key: 'subOrganization',
        label: 'sub organization',
        pluralLabel: 'Sub Organizations',
        List: SubOrganizationInstances,
    },
    vendor: { key: 'vendor', label: 'vendor', pluralLabel: 'Vendors', List: VendorInstances },
    webhook: { key: 'webhook', label: 'webhook', pluralLabel: 'Webhooks', List: WebhookInstances },
    taxCode: { key: 'taxCode', label: 'tax code', pluralLabel: 'Tax Codes', List: TaxCodeInstances },
    apiKey: { key: 'apiKey', label: 'API key', pluralLabel: 'API Keys', List: ApiKeyInstances },
};
